import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, CheckCircle2, X, ArrowRight, Mail, Phone } from "lucide-react";

const COVERED = [
  "Loose or lifting tile, plank, or board",
  "Grout cracks caused by improper installation",
  "Uneven seams, lippage, or crooked cuts",
  "Transitions or trim coming free from installation",
  "Shower waterproofing (where we installed the pan/membrane)",
];

const NOT_COVERED = [
  "Normal wear and tear from daily use",
  "Water damage from leaks, flooding, or appliance failure",
  "Damage from improper cleaning products or hard impacts",
  "Movement in the home's structure, framing, or sub-floor",
  "Repairs or alterations performed by anyone other than us",
  "Materials or products supplied by the customer",
];

export default function Warranty() {
  return (
    <div data-testid="warranty-page" className="max-w-5xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
      <div className="eyebrow text-[color:var(--brand-wood)]">Warranty &amp; Guarantees</div>
      <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
        1-Year Labor Warranty.<br/>
        <span className="italic text-[color:var(--brand-woodDark)]">In plain English.</span>
      </h1>
      <p className="mt-5 text-[color:var(--brand-woodDark)] max-w-2xl">
        Every flooring and tile installation performed by JFeva Solutions, LLC is backed by a full one-year warranty on our
        labor. Here&apos;s exactly what that means — no legal fog.
      </p>

      {/* Big promise card */}
      <div className="mt-10 rounded-3xl bg-[color:var(--brand-ink)] text-[color:var(--brand-cream)] p-10 lg:p-14 flex flex-wrap items-center gap-8" data-testid="warranty-headline">
        <div className="h-20 w-20 rounded-2xl bg-white/10 flex items-center justify-center shrink-0">
          <ShieldCheck className="h-10 w-10 text-[color:var(--brand-cream)]"/>
        </div>
        <div className="flex-1 min-w-[260px]">
          <div className="eyebrow text-white/60">Our promise</div>
          <div className="mt-2 font-display text-3xl sm:text-4xl leading-tight">
            For 12 months after we finish, if the installation fails — we fix it, free.
          </div>
          <p className="mt-3 text-white/70 text-sm">
            That includes labor, materials needed to make the repair, and travel. One phone call and we&apos;re on it.
          </p>
        </div>
      </div>

      {/* Covered / Not covered grid */}
      <div className="mt-12 grid gap-6 md:grid-cols-2">
        <div className="rounded-2xl border border-black/10 bg-white p-8" data-testid="warranty-covered">
          <div className="flex items-center gap-3">
            <span className="h-9 w-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
              <CheckCircle2 className="h-5 w-5"/>
            </span>
            <h2 className="font-display text-2xl">What&apos;s covered</h2>
          </div>
          <ul className="mt-5 space-y-3 text-sm">
            {COVERED.map((c) => (
              <li key={c} className="flex items-start gap-3">
                <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0"/>
                <span className="text-[color:var(--brand-woodDark)]">{c}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-2xl border border-black/10 bg-white p-8" data-testid="warranty-not-covered">
          <div className="flex items-center gap-3">
            <span className="h-9 w-9 rounded-full bg-[color:var(--brand-stone)] text-[color:var(--brand-woodDark)] flex items-center justify-center">
              <X className="h-5 w-5"/>
            </span>
            <h2 className="font-display text-2xl">What&apos;s not covered</h2>
          </div>
          <ul className="mt-5 space-y-3 text-sm">
            {NOT_COVERED.map((c) => (
              <li key={c} className="flex items-start gap-3">
                <X className="h-4 w-4 text-[color:var(--brand-woodDark)] mt-0.5 shrink-0"/>
                <span className="text-[color:var(--brand-woodDark)]">{c}</span>
              </li>
            ))}
          </ul>
          <p className="mt-5 text-[11px] text-[color:var(--brand-woodDark)]">
            Manufacturer product warranties on the flooring or tile itself pass through directly from the manufacturer.
          </p>
        </div>
      </div>

      {/* How to claim */}
      <div className="mt-12 rounded-2xl border border-black/10 bg-white p-8" data-testid="warranty-claim">
        <h2 className="font-display text-2xl">How to make a claim</h2>
        <ol className="mt-5 space-y-3 text-sm text-[color:var(--brand-woodDark)] list-decimal pl-5">
          <li>Reach out within 12 months of your project completion by email or phone.</li>
          <li>Share your project address, invoice number, and photos of the issue.</li>
          <li>We respond within 5 business days with an inspection date.</li>
          <li>We make the repair at no cost once the issue is confirmed under warranty.</li>
        </ol>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <a href="mailto:info@jfeva.com" data-testid="warranty-email"
            className="inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-ink)] text-[color:var(--brand-cream)] px-5 py-2.5 text-sm hover:-translate-y-0.5 transition-transform">
            <Mail className="h-4 w-4"/> info@jfeva.com
          </a>
          <a href="tel:8502520162" data-testid="warranty-phone"
            className="inline-flex items-center gap-2 rounded-full border border-black/15 px-5 py-2.5 text-sm hover:bg-black/5 transition-colors">
            <Phone className="h-4 w-4"/> 850-252-0162
          </a>
        </div>
      </div>

      {/* Related links */}
      <div className="mt-12 flex flex-wrap gap-3 text-xs">
        <Link data-testid="warranty-to-process" to="/what-to-expect" className="rounded-full border border-black/10 px-4 py-2 hover:bg-black/5">What to expect</Link>
        <Link data-testid="warranty-to-terms" to="/terms" className="rounded-full border border-black/10 px-4 py-2 hover:bg-black/5">Full Terms &amp; Conditions</Link>
        <Link data-testid="warranty-to-refund" to="/refund" className="rounded-full border border-black/10 px-4 py-2 hover:bg-black/5">Refund Policy</Link>
        <Link data-testid="warranty-to-quote" to="/quote" className="ml-auto inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-ink)] text-[color:var(--brand-cream)] px-4 py-2 hover:-translate-y-0.5 transition-transform">
          Start your quote <ArrowRight className="h-3.5 w-3.5"/>
        </Link>
      </div>
    </div>
  );
}
